import { Calendar as CalendarSVG } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useIntersection } from 'react-use';
import { ALL_TAB, FOR_YOU } from '../../../../constants/tabConfig';
import {
    Buckets,
    ContentShape,
    EventSearch,
    FeatureFlags,
    FeedConfigShape,
} from '../../../../types/index';
import { LoadingBucket } from '../../components/LoadingBucket';
import { HEAP_SCROLLED_TO_BOTTOM } from '../../constants/analytics';
import {
    getBucketsContent,
    getFlatBucketContent,
    verifyPoorContent,
} from '../../redux/content';
import { buildBuckets, getBuckets } from '../../utils';
import { BucketContainer } from '../BucketContainer';
import { CategoryTiles } from '../CategoryTiles';
import { LazyLoadedCollectionsBucket } from '../CollectionsBucketContainer';
import './FeedBuilderContainer.scss';

const StateMessage = loadable(() => import('../../components/StateMessage'), {
    resolveComponent: (components: { StateMessage: React.ReactElement }) =>
        components.StateMessage,
});

const THRESHOLDS = [0, 0.05, 0.1, 0.15];

const useIsInWindow = () => {
    const [isInWindow, setIsInWindow] = React.useState(false);
    const intersectionRef = React.useRef(null);

    const intersection = useIntersection(intersectionRef, {
        root: null,
        rootMargin: '0px',
        threshold: THRESHOLDS,
    });

    const isIntersecting = intersection?.intersectionRatio >= 0.05;

    useEffect(() => {
        if (isIntersecting) {
            setIsInWindow(true);
        }
    }, [isIntersecting]);

    return {
        ref: intersectionRef,
        isInWindow,
    };
};

const EmptyState = loadable(() => import('../../components/EmptyState'), {
    resolveComponent: (components: { EmptyState: React.ReactElement }) =>
        components.EmptyState,
});

interface FeedBuilderTabsProps {
    buckets: Buckets;
    events: Buckets;
    featureFlags: FeatureFlags;
    feedConfig: FeedConfigShape;
    eventSearch: EventSearch;
    verifyPoorContent?: Function;
    shouldSuggestReloadingPage?: boolean;
    tabKey?: string;
    isAuthenticated?: boolean;
    loadingState: {
        isFeLoading?: boolean;
        isSearchLoading?: boolean;
        isCityBrowseLoading?: boolean;
        isProfilesLoading?: boolean;
        isOrganizerArticlesLoading?: boolean;
    };
}

const FeedBuilderTabs = (props: FeedBuilderTabsProps) => {
    const {
        tabKey,
        buckets,
        events,
        featureFlags,
        feedConfig,
        verifyPoorContent,
        loadingState: {
            isFeLoading,
            isSearchLoading,
            isCityBrowseLoading,
        } = {},
        shouldSuggestReloadingPage,
    } = props;

    const { isInWindow, ref } = useIsInWindow();
    const { isInWindow: bottomIsInWindow, ref: bottomPageRef } =
        useIsInWindow();
    const [bottomTracked, setBottomTracked] = useState(false);
    const isForYou = tabKey === FOR_YOU;

    verifyPoorContent?.({
        buckets,
        events,
        actionName: 'on_tab_loading',
    });

    let renderedBuckets: React.ReactNode = [
        <div className="feed-events-bucket" key="loading-component">
            <div
                data-testid="feed-events-bucket-content"
                className="feed-events-bucket__content feed-events-bucket__content--loading"
            >
                <LoadingBucket />
            </div>
        </div>,
    ];

    // track scroll to bottom
    if (bottomIsInWindow && !bottomTracked) {
        setBottomTracked(true);
        logEvent(HEAP_SCROLLED_TO_BOTTOM);
    }

    // Only show this when in browser, not
    //server, to avoid hydration mismatch
    if (!isFeLoading && isInWindow) {
        const allFlatBuckets = buildBuckets({
            events,
            buckets,
            feedConfig,
            isCityBrowseLoading,
        });

        const allBuckets = isForYou
            ? buckets
            : getBuckets({
                  buckets: allFlatBuckets,
                  featureFlags,
                  feedConfig,
              });

        if (allBuckets.length === 0 && shouldSuggestReloadingPage) {
            // The server is temporarily unable to return events
            return (
                <StateMessage
                    title={gettext('Unable to load events')}
                    description={gettext('Try again or check back later')}
                    iconType={<CalendarSVG />}
                    iconSize="medium"
                    primaryLink="/"
                    primaryText={gettext('Try again')}
                />
            );
        }

        if (!isSearchLoading) {
            const allBucketsContent = allBuckets.filter(
                (bucket) => 'events' in bucket || 'profiles' in bucket,
            );

            if (allBucketsContent.length > 0) {
                renderedBuckets = allBucketsContent.reduce(
                    (
                        memo: $FixMe,
                        { key, type, bucketConfig, ...bucketProps },
                        index,
                    ) => {
                        const lastIndex = allBucketsContent.length - 1;
                        const separator =
                            index < lastIndex ? (
                                <hr
                                    key={`separator-${key}`}
                                    className="feed-events-bucket__separator"
                                />
                            ) : null;

                        if (type === 'event' || type === 'profile') {
                            return [
                                ...(memo[Symbol.iterator] ? memo : []),
                                <BucketContainer
                                    key={`${type}-${key}`}
                                    type={type}
                                    bucketConfig={bucketConfig}
                                    bucketKey={key}
                                    // Always show see more for "Organizers you follow"
                                    shouldShowSeeMore={
                                        (index === lastIndex ||
                                            key === 'followed_profiles') &&
                                        !isForYou
                                    }
                                    featureFlags={featureFlags}
                                    bucketIndex={index}
                                    {...bucketProps}
                                />,
                                tabKey === ALL_TAB && index === 0 ? (
                                    <LazyLoadedCollectionsBucket
                                        key={`collection-${key}`}
                                    />
                                ) : null,
                                separator,
                            ];
                        }

                        return memo;
                    },
                    [],
                );
            } else {
                renderedBuckets = (
                    <EmptyState
                        bucketKey={FOR_YOU}
                        emptyState={[
                            {
                                validation: {
                                    shouldShowEmptyState: true,
                                },
                                config: {
                                    title: gettext("Let's make it personal"),
                                    iconType: 'EmptyState',
                                    iconSize: 'xlarge',
                                    description: gettext(
                                        'Log in and tell us what you love to get event picks based on your interests',
                                    ),
                                    primaryText: gettext('Get started'),
                                    primaryLink: '/login/?referrer=%2F',
                                    buttonStyle: 'fill',
                                },
                            },
                        ]}
                    />
                );
            }
        }
    }

    return (
        <div className="feed-builder" ref={ref}>
            {tabKey === ALL_TAB ? <CategoryTiles /> : null}

            {renderedBuckets}
            <div ref={bottomPageRef}></div>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    content,
    currencyFormat,
    user,
}: {
    app: {
        featureFlags: Pick<FeedBuilderTabsProps, 'featureFlags'>;
        loadingState: Pick<FeedBuilderTabsProps, 'loadingState'>;
    };
    content: ContentShape;
    currencyFormat: string;
    user: { isAuthenticated: boolean };
}) => {
    return {
        buckets: getBucketsContent(content, currencyFormat),
        events: getFlatBucketContent(
            content.flatBucket,
            currencyFormat,
            {},
            content.browseState?.tabKey || '',
        ),
        featureFlags: app.featureFlags,
        loadingState: app.loadingState,
        shouldSuggestReloadingPage:
            !!content.flatBucket?.__should_suggest_reloading_page,
        tabKey: content.browseState?.tabKey,
        isAuthenticated: user.isAuthenticated,
    };
};

const _mapDispatchToProps = {
    verifyPoorContent,
};

export default connect(_mapStateToProps, _mapDispatchToProps)(FeedBuilderTabs);
